import React, { useEffect, useState } from 'react';
import { X, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import Button from '../common/Button.js';
import SelectField from '../common/SelectField.js';
import SearchableFilterSelect from '../common/SearchableFilterSelect.js';
import Alert from '../common/Alert';
import cashbookService from '../../services/CashbookService.js';
import { formatLocationDropdownLabel, sortLocationsByNumber } from '../../utils/locationDropdownLabel.js';

const getToday = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const formatCashbookUserOption = (u) => {
  const name = (u?.name || u?.username || u?.user_id || '—').trim();
  const hub = (
    u?.location_hub_label
    || u?.location_hub_name
    || u?.location
    || u?.city
    || '—'
  ).trim();
  const role = (u?.role_name || u?.role || '—').trim();
  return `${name} - ${hub} - ${role}`;
};

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
];

const normalizePaymentMode = (mode) => (
  String(mode || '').trim().toLowerCase() === 'cash' ? 'cash' : 'upi'
);

const emptyForm = (defaults = {}) => ({
  direction: 'in',
  entry_date: getToday(),
  payment_mode: 'cash',
  amount: '',
  party_name: '',
  reference_no: '',
  description: '',
  location_hub_id: defaults.location_hub_id || '',
  owner_user_id: defaults.owner_user_id || '',
});

const CashbookEntryModal = ({
  open,
  onClose,
  onSaved,
  entry,
  locationHubs = [],
  defaultLocationHubId = '',
  hubUsers = [],
  defaultOwnerUserId = '',
  requireHub = false,
}) => {
  const [form, setForm] = useState(() => emptyForm({
    location_hub_id: defaultLocationHubId,
    owner_user_id: defaultOwnerUserId,
  }));
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(entry);

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setForm({
        direction: entry.type === 'received' ? 'in' : 'out',
        entry_date: entry.date || getToday(),
        payment_mode: normalizePaymentMode(entry.payment_mode || entry.account),
        amount: String(entry.amount ?? ''),
        party_name: entry.party_name || '',
        reference_no: entry.reference_no || '',
        description: entry.details || '',
        location_hub_id: entry.location_hub_id != null ? String(entry.location_hub_id) : (defaultLocationHubId || ''),
        owner_user_id: entry.owner_user_id || defaultOwnerUserId || '',
      });
    } else {
      setForm(emptyForm({
        location_hub_id: defaultLocationHubId,
        owner_user_id: defaultOwnerUserId,
      }));
    }
  }, [open, entry, defaultLocationHubId, defaultOwnerUserId]);

  if (!open) return null;

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) {
      Alert.error('Enter a valid amount');
      return;
    }
    if (!isEdit && requireHub && !form.location_hub_id) {
      Alert.error('Select the hub/branch this entry belongs to');
      return;
    }
    setSaving(true);
    const paymentMode = normalizePaymentMode(form.payment_mode);
    const payload = {
      direction: form.direction,
      entry_date: form.entry_date,
      payment_mode: paymentMode,
      account_type: paymentMode === 'cash' ? 'cash' : 'bank',
      amount,
      party_name: form.party_name || null,
      reference_no: form.reference_no || null,
      description: form.description || null,
      ...(form.location_hub_id ? { location_hub_id: Number(form.location_hub_id) } : {}),
      ...(form.owner_user_id ? { owner_user_id: form.owner_user_id } : {}),
    };
    try {
      if (isEdit) {
        await cashbookService.updateEntry(entry.id, payload);
        Alert.success('Entry updated');
      } else {
        await cashbookService.createEntry(payload);
        Alert.success('Entry added');
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const isIn = form.direction === 'in';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-base font-semibold text-gray-800">
            {isEdit ? 'Edit Cashbook Entry' : 'Add Cashbook Entry'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setField('direction', 'in')}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium ${
                isIn ? 'bg-green-50 border-green-400 text-green-700' : 'border-gray-300 text-gray-600'
              }`}
            >
              <ArrowDownLeft className="w-4 h-4" /> Received
            </button>
            <button
              type="button"
              onClick={() => setField('direction', 'out')}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium ${
                !isIn ? 'bg-red-50 border-red-400 text-red-700' : 'border-gray-300 text-gray-600'
              }`}
            >
              <ArrowUpRight className="w-4 h-4" /> Paid
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!isEdit && locationHubs.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hub / Branch {requireHub ? '*' : ''}
                </label>
                <SearchableFilterSelect
                  value={form.location_hub_id}
                  onChange={(e) => setField('location_hub_id', e.target.value)}
                  placeholder="Select hub"
                  options={[
                    { value: '', label: 'Select hub' },
                    ...sortLocationsByNumber(locationHubs).map((hub) => ({
                      value: hub.id,
                      label: formatLocationDropdownLabel(hub, true),
                    })),
                  ]}
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Financials go to this branch even if you are logged in at another location.
                </p>
              </div>
            )}

            {!isEdit && hubUsers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User book (optional)</label>
                <SearchableFilterSelect
                  value={form.owner_user_id}
                  onChange={(e) => setField('owner_user_id', e.target.value)}
                  placeholder="Branch book only"
                  options={[
                    { value: '', label: 'Branch book only' },
                    ...hubUsers.map((u) => ({
                      value: u.user_id || u.id,
                      label: formatCashbookUserOption(u),
                    })),
                  ]}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date" value={form.entry_date}
                onChange={(e) => setField('entry_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
              <input
                type="number" step="0.01" value={form.amount}
                onChange={(e) => setField('amount', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
              <SelectField
                variant="form"
                value={form.payment_mode}
                onChange={(e) => setField('payment_mode', e.target.value)}
                className="w-full"
              >
                {PAYMENT_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </SelectField>
              <p className="text-[11px] text-gray-500 mt-1">
                Cash posts to Cash balance; UPI posts to UPI balance.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Party / Name</label>
              <input
                type="text" value={form.party_name}
                onChange={(e) => setField('party_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                placeholder="e.g. Bus Agent - NAIK TOURS"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference No.</label>
              <input
                type="text" value={form.reference_no}
                onChange={(e) => setField('reference_no', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
              <textarea
                rows={2} value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                placeholder="Description"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" loading={saving} disabled={saving}>
              {isEdit ? 'Update' : 'Add Entry'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CashbookEntryModal;
